import { assoc, CorrectLens, curry, map, view } from "ramda";
import { reduceTreeChildren } from "../../../utils/mapReduceTree";
import {
  AccountDescription,
  AccountId,
  Spread,
  SpreadAccount
} from "../spread/types";
import {
  spreadBalanceSheetTopLevelChildren,
  spreadCashFlowTopLevelChildren,
  spreadIncomeStatementTopLevelChildren
} from "./lenses";
import { AccountMap } from "./types";

export type AccountDescriptions = AccountMap<AccountDescription>;

export const makeAccountDescription = (
  accountFromBackend: SpreadAccount
): AccountDescription => ({
  id: accountFromBackend.id,
  name: accountFromBackend.name,
  type: accountFromBackend.type,
  formula: accountFromBackend.formula,
  clazz: accountFromBackend.clazz,
  exceptionEligible: accountFromBackend.value.exceptionEligible
});

export const buildAccountDescriptions = curry(
  (
    baseLens: CorrectLens<Spread, SpreadAccount[]>,
    spread: Spread | undefined
  ) => {
    if (!spread) {
      return {};
    }
    const topLevelChildren = view(baseLens, spread);
    return reduceTreeChildren<SpreadAccount, AccountDescriptions>(
      (account: SpreadAccount) => account.children,
      (accountDescriptions: AccountDescriptions, account: SpreadAccount) =>
        assoc(account.id, makeAccountDescription(account), accountDescriptions),
      {}
    )(topLevelChildren);
  }
);

export const buildIncomeStatementAccountDescriptions = buildAccountDescriptions(
  spreadIncomeStatementTopLevelChildren
);
export const buildBalanceSheetAccountDescriptions = buildAccountDescriptions(
  spreadBalanceSheetTopLevelChildren
);
export const buildCashFlowAccountDescriptions = buildAccountDescriptions(
  spreadCashFlowTopLevelChildren
);

export const getAccountDescription = curry(
  (accountDescriptions: AccountDescriptions, accountId: AccountId) =>
    accountDescriptions[accountId]
);

export const mapAccountDescriptions = <T>(
  transformFn: (accountDescription: AccountDescription) => T
) => (accountDescriptions: AccountDescriptions): AccountMap<T> => {
  return map(transformFn, accountDescriptions);
};



// WEBPACK FOOTER //
// ./src/spread/model/derivedModel/accountDescriptions.ts